import React, { useEffect, useState } from "react";
import moment from "moment";
import { Plus, Trash2, TrendingUp, Zap, Pencil } from "lucide-react";
import { Modal } from "react-bootstrap";
import { Pagination, Stack } from "@mui/material";
import { Loader } from "../loader";
import api from "../../../api";
import "../AddPost.css";
import "./Live.css";
import "../AdminMarket.css";

const INITIAL_SPOT = {
  exchange: "",
  coin: "",
  entryPrice: "",
  takeProfit: "",
  stopLoss: "",
  filledAmountUSDT: "",
  filledQuantityCoin: "",
  status: "Open",
};

const INITIAL_FUTURES = {
  exchange: "",
  coin: "",
  positionType: "",
  leverage: "",
  marginMode: "",
  entryPrice: "",
  stopLoss: "",
  takeProfit: "",
  marginUsedUSDT: "",
  positionSizeUSDT: "",
  liquidationPrice: "",
  status: "Open",
  pnlUSDT: "",
  roiPercent: "",
};

function LiveSessionTrade() {
  const [trades, setTrades] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loader, setLoader] = useState(true);
  const [submitLoader, setSubmitLoader] = useState(false);

  const [show, setShow] = useState(false);
  const [tradeType, setTradeType] = useState(null);
  const [spotTrade, setSpotTrade] = useState(INITIAL_SPOT);
  const [futuresTrade, setFuturesTrade] = useState(INITIAL_FUTURES);
  const [error, setError] = useState("");

  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTradeType, setEditTradeType] = useState(null);
  const [editSpotTrade, setEditSpotTrade] = useState(INITIAL_SPOT);
  const [editFuturesTrade, setEditFuturesTrade] = useState(INITIAL_FUTURES);
  const [editError, setEditError] = useState("");
  const [editLoader, setEditLoader] = useState(false);

  const fetchTrades = async (p = 1) => {
    try {
      setLoader(true);
      const res = await api.liveSessionTradeGetAdmin({ page: p });
      if (res?.success) {
        setTrades(res.trades);
        setTotalPages(Math.ceil(res.count / res.limit) || 1);
      }
    } catch (e) {
      console.error(e.message);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { fetchTrades(page); }, [page]);

  const handleSpotChange = (e) =>
    setSpotTrade({ ...spotTrade, [e.target.name]: e.target.value });

  const handleFuturesChange = (e) =>
    setFuturesTrade({ ...futuresTrade, [e.target.name]: e.target.value });

  const handleTradeTypeChange = (type) =>
    setTradeType(type === tradeType ? null : type);

  const resetForm = () => {
    setTradeType(null);
    setSpotTrade(INITIAL_SPOT);
    setFuturesTrade(INITIAL_FUTURES);
    setError("");
  };

  const handleClose = () => { resetForm(); setShow(false); };

  const validate = () => {
    if (!tradeType) { setError("Please select Spot or Futures."); return false; }
    if (tradeType === "spot") {
      const req = ["coin", "entryPrice", "takeProfit", "filledAmountUSDT", "filledQuantityCoin"];
      const missing = req.filter((f) => !spotTrade[f]);
      if (missing.length) { setError(`Missing: ${missing.join(", ")}`); return false; }
    }
    if (tradeType === "futures") {
      const req = ["coin", "positionType", "entryPrice", "stopLoss", "takeProfit"];
      const missing = req.filter((f) => !futuresTrade[f]);
      if (missing.length) { setError(`Missing: ${missing.join(", ")}`); return false; }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitLoader(true);
    setError("");
    try {
      const res = await api.liveSessionTradeAdd({
        tradeType,
        spotTrade: tradeType === "spot" ? spotTrade : null,
        futuresTrade: tradeType === "futures" ? futuresTrade : null,
      });
      if (res?.success) {
        handleClose();
        fetchTrades(1);
        setPage(1);
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this trade?")) return;
    try {
      await api.liveSessionTradeDelete({ _id: id });
      fetchTrades(page);
    } catch (e) {
      console.error(e.message);
    }
  };

  const handleEditOpen = (t) => {
    setEditId(t._id);
    setEditTradeType(t.tradeType);
    if (t.tradeType === "spot") {
      setEditSpotTrade({ ...INITIAL_SPOT, ...t.spotTrade });
      setEditFuturesTrade(INITIAL_FUTURES);
    } else {
      setEditFuturesTrade({ ...INITIAL_FUTURES, ...t.futuresTrade });
      setEditSpotTrade(INITIAL_SPOT);
    }
    setEditError("");
    setShowEdit(true);
  };

  const handleEditClose = () => {
    setShowEdit(false);
    setEditId(null);
    setEditTradeType(null);
    setEditSpotTrade(INITIAL_SPOT);
    setEditFuturesTrade(INITIAL_FUTURES);
    setEditError("");
  };

  const handleEditSubmit = async () => {
    if (!editTradeType) { setEditError("Trade type missing."); return; }
    setEditLoader(true);
    setEditError("");
    try {
      const res = await api.liveSessionTradeEdit({
        _id: editId,
        tradeType: editTradeType,
        spotTrade: editTradeType === "spot" ? editSpotTrade : null,
        futuresTrade: editTradeType === "futures" ? editFuturesTrade : null,
      });
      if (res?.success) {
        handleEditClose();
        fetchTrades(page);
      }
    } catch (e) {
      setEditError(e?.response?.data?.message || "Something went wrong");
    } finally {
      setEditLoader(false);
    }
  };

  const tradeData = (t) => t.tradeType === "spot" ? t.spotTrade : t.futuresTrade;

  const statusClass = (s) => {
    if (s === "Open") return "badge bg-success";
    if (s === "TP Hit") return "badge bg-primary";
    if (s === "SL Hit") return "badge bg-danger";
    return "badge bg-secondary";
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-3 d-flex align-items-center justify-content-center"
            style={{ width: 56, height: 56, background: "linear-gradient(135deg,#7c3aed,#a855f7)" }}
          >
            <TrendingUp size={26} color="#fff" />
          </div>
          <div>
            <h4 className="mb-0 fw-700">Live Session Trades</h4>
            <span className="badge" style={{ background: "#7c3aed", fontSize: 12 }}>
              {trades.length} trade{trades.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <button
          className="btn text-white fw-600 d-flex align-items-center gap-2"
          style={{ background: "#7c3aed", borderRadius: 10 }}
          onClick={() => setShow(true)}
        >
          <Plus size={18} /> Add Trade
        </button>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-body p-0">
          {loader ? (
            <div className="py-5"><Loader /></div>
          ) : trades.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <TrendingUp size={40} className="mb-3 opacity-25" />
              <p className="mb-0">No trades yet. Click <strong>Add Trade</strong> to get started.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: "#f8f7ff" }}>
                  <tr>
                    <th className="ps-4 py-3 text-muted fw-600" style={{ fontSize: 13 }}>#</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>TYPE</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>COIN</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>ENTRY</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>TP</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>SL</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>STATUS</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>CREATED AT</th>
                    <th className="py-3 text-muted fw-600" style={{ fontSize: 13 }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t, i) => {
                    const d = tradeData(t);
                    return (
                      <tr key={t._id}>
                        <td className="ps-4 text-muted" style={{ fontSize: 13 }}>
                          {(page - 1) * 10 + i + 1}
                        </td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              background: t.tradeType === "spot" ? "#d1fae5" : "#ede9fe",
                              color: t.tradeType === "spot" ? "#065f46" : "#5b21b6",
                              fontSize: 12,
                            }}
                          >
                            {t.tradeType === "spot"
                              ? <><TrendingUp size={11} className="me-1" />Spot</>
                              : <><Zap size={11} className="me-1" />Futures</>}
                          </span>
                        </td>
                        <td className="fw-600">{d?.coin || "-"}</td>
                        <td>{d?.entryPrice ?? "-"}</td>
                        <td>{d?.takeProfit ?? "-"}</td>
                        <td>{d?.stopLoss ?? "-"}</td>
                        <td>
                          <span className={statusClass(d?.status)} style={{ fontSize: 12 }}>
                            {d?.status}
                          </span>
                        </td>
                        <td className="text-muted" style={{ fontSize: 13 }}>
                          {t.createdAt ? moment(t.createdAt).format("MMM D, YYYY h:mm A") : "-"}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleEditOpen(t)}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(t._id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, val) => setPage(val)}
              color="primary"
            />
          </Stack>
        </div>
      )}

      {/* Edit Trade Modal */}
      <Modal show={showEdit} onHide={handleEditClose} size="xl" centered className="modern-modal">
        <Modal.Body className="modern-modal-body">
          <div className="modal-header-section">
            <h3 className="modal-title">Edit Live Session Trade</h3>
            <button className="modal-close-btn" onClick={handleEditClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-content-section">
            {editError && (
              <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>
                {editError}
              </div>
            )}

            {editTradeType === "spot" && (
              <div className="trade-fields-container trade-fields-spot">
                <div className="trade-fields-badge"><i className="fas fa-coins"></i> Spot Trade</div>
                <div className="trade-grid">
                  <div className="trade-field">
                    <label className="trade-label">Exchange</label>
                    <input type="text" name="exchange" className="trade-input"
                      placeholder="e.g. Binance" value={editSpotTrade.exchange}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Coin <span className="required-star">*</span></label>
                    <input type="text" name="coin" className="trade-input"
                      placeholder="e.g. BTC" value={editSpotTrade.coin}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Entry Price – Buy Limit <span className="required-star">*</span></label>
                    <input type="number" name="entryPrice" className="trade-input"
                      value={editSpotTrade.entryPrice}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Take Profit – Sell Limit <span className="required-star">*</span></label>
                    <input type="number" name="takeProfit" className="trade-input"
                      value={editSpotTrade.takeProfit}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Stop Loss (SL)</label>
                    <input type="number" name="stopLoss" className="trade-input"
                      value={editSpotTrade.stopLoss}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Filled Amount (USDT) <span className="required-star">*</span></label>
                    <input type="number" name="filledAmountUSDT" className="trade-input"
                      value={editSpotTrade.filledAmountUSDT}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Filled Quantity (COIN) <span className="required-star">*</span></label>
                    <input type="number" name="filledQuantityCoin" className="trade-input"
                      value={editSpotTrade.filledQuantityCoin}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Status <span className="required-star">*</span></label>
                    <select name="status" className="trade-input trade-select"
                      value={editSpotTrade.status}
                      onChange={(e) => setEditSpotTrade({ ...editSpotTrade, [e.target.name]: e.target.value })}>
                      <option value="Open">Open</option>
                      <option value="Closed">Closed</option>
                      <option value="TP Hit">TP Hit</option>
                      <option value="SL Hit">SL Hit</option>
                    </select>
                  </div>
                </div>
                <p className="trade-required-note"><span className="required-star">*</span> Required fields</p>
              </div>
            )}

            {editTradeType === "futures" && (
              <div className="trade-fields-container trade-fields-futures">
                <div className="trade-fields-badge futures-badge"><i className="fas fa-bolt"></i> Futures Trade</div>
                <div className="trade-grid">
                  <div className="trade-field">
                    <label className="trade-label">Exchange</label>
                    <input type="text" name="exchange" className="trade-input"
                      value={editFuturesTrade.exchange}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Coin <span className="required-star">*</span></label>
                    <input type="text" name="coin" className="trade-input"
                      value={editFuturesTrade.coin}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Position Type <span className="required-star">*</span></label>
                    <select name="positionType" className="trade-input trade-select"
                      value={editFuturesTrade.positionType}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })}>
                      <option value="">Select</option>
                      <option value="Long">Long</option>
                      <option value="Short">Short</option>
                    </select>
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Leverage</label>
                    <div className="trade-input-suffix">
                      <input type="number" name="leverage" className="trade-input"
                        value={editFuturesTrade.leverage}
                        onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                      <span className="suffix-label">x</span>
                    </div>
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Margin Mode</label>
                    <select name="marginMode" className="trade-input trade-select"
                      value={editFuturesTrade.marginMode}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })}>
                      <option value="">Select</option>
                      <option value="Cross">Cross</option>
                      <option value="Isolated">Isolated</option>
                    </select>
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Entry Price <span className="required-star">*</span></label>
                    <input type="number" name="entryPrice" className="trade-input"
                      value={editFuturesTrade.entryPrice}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Stop Loss (SL) <span className="required-star">*</span></label>
                    <input type="number" name="stopLoss" className="trade-input"
                      value={editFuturesTrade.stopLoss}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Take Profit (TP) <span className="required-star">*</span></label>
                    <input type="number" name="takeProfit" className="trade-input"
                      value={editFuturesTrade.takeProfit}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Margin Used (USDT)</label>
                    <input type="number" name="marginUsedUSDT" className="trade-input"
                      value={editFuturesTrade.marginUsedUSDT}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Position Size (USDT)</label>
                    <input type="number" name="positionSizeUSDT" className="trade-input"
                      value={editFuturesTrade.positionSizeUSDT}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Liquidation Price</label>
                    <input type="number" name="liquidationPrice" className="trade-input"
                      value={editFuturesTrade.liquidationPrice}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">Status <span className="required-star">*</span></label>
                    <select name="status" className="trade-input trade-select"
                      value={editFuturesTrade.status}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })}>
                      <option value="Open">Open</option>
                      <option value="Closed">Closed</option>
                      <option value="TP Hit">TP Hit</option>
                      <option value="SL Hit">SL Hit</option>
                    </select>
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">PNL (USDT)</label>
                    <input type="number" name="pnlUSDT" className="trade-input"
                      value={editFuturesTrade.pnlUSDT}
                      onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                  </div>
                  <div className="trade-field">
                    <label className="trade-label">ROI (%)</label>
                    <div className="trade-input-suffix">
                      <input type="number" name="roiPercent" className="trade-input"
                        value={editFuturesTrade.roiPercent}
                        onChange={(e) => setEditFuturesTrade({ ...editFuturesTrade, [e.target.name]: e.target.value })} />
                      <span className="suffix-label">%</span>
                    </div>
                  </div>
                </div>
                <p className="trade-required-note"><span className="required-star">*</span> Required fields</p>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-light fw-600 px-4" onClick={handleEditClose}>Cancel</button>
              <button
                className="btn text-white fw-600 px-4"
                style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none" }}
                onClick={handleEditSubmit}
                disabled={editLoader}
              >
                {editLoader ? "Saving..." : "Update Trade"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Trade Modal */}
      <Modal show={show} onHide={handleClose} size="xl" centered className="modern-modal">
        <Modal.Body className="modern-modal-body">
          <div className="modal-header-section">
            <h3 className="modal-title">Add Live Session Trade</h3>
            <button className="modal-close-btn" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-content-section">
          {error && (
            <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Trade Type Toggle */}
          <div className="trade-type-toggle mb-3">
            <button
              type="button"
              className={`trade-type-btn ${tradeType === "spot" ? "active-spot" : ""}`}
              onClick={() => handleTradeTypeChange("spot")}
            >
              <i className="fas fa-coins me-2"></i>Spot Trade
            </button>
            <button
              type="button"
              className={`trade-type-btn ${tradeType === "futures" ? "active-futures" : ""}`}
              onClick={() => handleTradeTypeChange("futures")}
            >
              <i className="fas fa-bolt me-2"></i>Futures Trade
            </button>
            {tradeType && (
              <button type="button" className="trade-type-btn trade-type-clear" onClick={() => setTradeType(null)}>
                <i className="fas fa-times me-2"></i>Clear
              </button>
            )}
          </div>

          {/* Spot Fields */}
          {tradeType === "spot" && (
            <div className="trade-fields-container trade-fields-spot">
              <div className="trade-fields-badge"><i className="fas fa-coins"></i> Spot Trade</div>
              <div className="trade-grid">
                <div className="trade-field">
                  <label className="trade-label">Exchange</label>
                  <input type="text" name="exchange" className="trade-input"
                    placeholder="e.g. Binance" value={spotTrade.exchange} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Coin <span className="required-star">*</span></label>
                  <input type="text" name="coin" className="trade-input"
                    placeholder="e.g. BTC" value={spotTrade.coin} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Entry Price – Buy Limit <span className="required-star">*</span></label>
                  <input type="number" name="entryPrice" className="trade-input"
                    placeholder="e.g. 67500" value={spotTrade.entryPrice} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Take Profit – Sell Limit <span className="required-star">*</span></label>
                  <input type="number" name="takeProfit" className="trade-input"
                    placeholder="e.g. 72000" value={spotTrade.takeProfit} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Stop Loss (SL)</label>
                  <input type="number" name="stopLoss" className="trade-input"
                    placeholder="e.g. 63000 (optional)" value={spotTrade.stopLoss} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Filled Amount (USDT) <span className="required-star">*</span></label>
                  <input type="number" name="filledAmountUSDT" className="trade-input"
                    placeholder="e.g. 1000" value={spotTrade.filledAmountUSDT} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Filled Quantity (COIN) <span className="required-star">*</span></label>
                  <input type="number" name="filledQuantityCoin" className="trade-input"
                    placeholder="e.g. 0.01481" value={spotTrade.filledQuantityCoin} onChange={handleSpotChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Status <span className="required-star">*</span></label>
                  <select name="status" className="trade-input trade-select"
                    value={spotTrade.status} onChange={handleSpotChange}>
                    <option value="Open">Open</option>
                    <option value="Closed">Closed</option>
                    <option value="TP Hit">TP Hit</option>
                    <option value="SL Hit">SL Hit</option>
                  </select>
                </div>
              </div>
              <p className="trade-required-note"><span className="required-star">*</span> Required fields</p>
            </div>
          )}

          {/* Futures Fields */}
          {tradeType === "futures" && (
            <div className="trade-fields-container trade-fields-futures">
              <div className="trade-fields-badge futures-badge"><i className="fas fa-bolt"></i> Futures Trade</div>
              <div className="trade-grid">
                <div className="trade-field">
                  <label className="trade-label">Exchange</label>
                  <input type="text" name="exchange" className="trade-input"
                    placeholder="e.g. Bybit" value={futuresTrade.exchange} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Coin <span className="required-star">*</span></label>
                  <input type="text" name="coin" className="trade-input"
                    placeholder="e.g. ETH" value={futuresTrade.coin} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Position Type <span className="required-star">*</span></label>
                  <select name="positionType" className="trade-input trade-select"
                    value={futuresTrade.positionType} onChange={handleFuturesChange}>
                    <option value="">Select</option>
                    <option value="Long">Long</option>
                    <option value="Short">Short</option>
                  </select>
                </div>
                <div className="trade-field">
                  <label className="trade-label">Leverage</label>
                  <div className="trade-input-suffix">
                    <input type="number" name="leverage" className="trade-input"
                      placeholder="e.g. 10" value={futuresTrade.leverage} onChange={handleFuturesChange} />
                    <span className="suffix-label">x</span>
                  </div>
                </div>
                <div className="trade-field">
                  <label className="trade-label">Margin Mode</label>
                  <select name="marginMode" className="trade-input trade-select"
                    value={futuresTrade.marginMode} onChange={handleFuturesChange}>
                    <option value="">Select</option>
                    <option value="Cross">Cross</option>
                    <option value="Isolated">Isolated</option>
                  </select>
                </div>
                <div className="trade-field">
                  <label className="trade-label">Entry Price <span className="required-star">*</span></label>
                  <input type="number" name="entryPrice" className="trade-input"
                    placeholder="e.g. 3200" value={futuresTrade.entryPrice} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Stop Loss (SL) <span className="required-star">*</span></label>
                  <input type="number" name="stopLoss" className="trade-input"
                    placeholder="e.g. 3050" value={futuresTrade.stopLoss} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Take Profit (TP) <span className="required-star">*</span></label>
                  <input type="number" name="takeProfit" className="trade-input"
                    placeholder="e.g. 3600" value={futuresTrade.takeProfit} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Margin Used (USDT)</label>
                  <input type="number" name="marginUsedUSDT" className="trade-input"
                    placeholder="e.g. 500" value={futuresTrade.marginUsedUSDT} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Position Size (USDT)</label>
                  <input type="number" name="positionSizeUSDT" className="trade-input"
                    placeholder="e.g. 5000" value={futuresTrade.positionSizeUSDT} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Liquidation Price</label>
                  <input type="number" name="liquidationPrice" className="trade-input"
                    placeholder="e.g. 2900" value={futuresTrade.liquidationPrice} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">Status <span className="required-star">*</span></label>
                  <select name="status" className="trade-input trade-select"
                    value={futuresTrade.status} onChange={handleFuturesChange}>
                    <option value="Open">Open</option>
                    <option value="Closed">Closed</option>
                    <option value="TP Hit">TP Hit</option>
                    <option value="SL Hit">SL Hit</option>
                  </select>
                </div>
                <div className="trade-field">
                  <label className="trade-label">PNL (USDT)</label>
                  <input type="number" name="pnlUSDT" className="trade-input"
                    placeholder="e.g. 320" value={futuresTrade.pnlUSDT} onChange={handleFuturesChange} />
                </div>
                <div className="trade-field">
                  <label className="trade-label">ROI (%)</label>
                  <div className="trade-input-suffix">
                    <input type="number" name="roiPercent" className="trade-input"
                      placeholder="e.g. 6.4" value={futuresTrade.roiPercent} onChange={handleFuturesChange} />
                    <span className="suffix-label">%</span>
                  </div>
                </div>
              </div>
              <p className="trade-required-note"><span className="required-star">*</span> Required fields</p>
            </div>
          )}

          {!tradeType && (
            <div className="text-center py-4 text-muted" style={{ fontSize: 14 }}>
              Select a trade type above to fill in the details.
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <button className="btn btn-light fw-600 px-4" onClick={handleClose}>Cancel</button>
            <button
              className="btn text-white fw-600 px-4"
              style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none" }}
              onClick={handleSubmit}
              disabled={submitLoader}
            >
              {submitLoader ? "Saving..." : "Save Trade"}
            </button>
          </div>
        </div>{/* end modal-content-section */}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default LiveSessionTrade;
